import { createTheme, Palette, Theme, ThemeOptions } from '@mui/material/styles';
import { APP_LOGO, APP_LOGO_LOGIN, APP_LOGO_TABLET, BACKGROUND, BACKGROUND_DARK, COMPLEMENTARY_1, COMPLEMENTARY_1_DARK, COMPLEMENTARY_2, COMPLEMENTARY_2_DARK, COMPLEMENTARY_3, COMPLEMENTARY_3_DARK, COMPLEMENTARY_4, COMPLEMENTARY_4_DARK, COMPLEMENTARY_5, COMPLEMENTARY_5_DARK, COMPLEMENTARY_6, COMPLEMENTARY_6_DARK, COMPLEMENTARY_7, COMPLEMENTARY_7_DARK, COMPLEMENTARY_8, COMPLEMENTARY_8_DARK, COMPLEMENTARY_9, COMPLEMENTARY_9_DARK, ERROR, ERROR_DARK, LOGIN_BACKGROUND, LOGIN_BACKGROUND_DARK, NEUTRAL_0, NEUTRAL_0_DARK, NEUTRAL_1, NEUTRAL_1_DARK, NEUTRAL_2, NEUTRAL_2_DARK, NEUTRAL_3, NEUTRAL_3_DARK, NEUTRAL_4, NEUTRAL_4_DARK, NEUTRAL_5, NEUTRAL_5_DARK, PRIMARY, PRIMARY_DARK, SECONDARY, SECONDARY_DARK, SUCCESS, SUCCESS_DARK, TERTIARY, WARNING, WARNING_DARK } from './config';

declare module '@mui/material/styles' {
    interface TypographyVariants {
        body3: React.CSSProperties;
        caption2: React.CSSProperties;
        button2: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        body3?: React.CSSProperties;
        caption2?: React.CSSProperties;
        button2?: React.CSSProperties;
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        body3: true;
        caption2: true;
        button2: true;
    }
}

interface Complementary {
    P: string,
    B: string
}

interface AppPalette extends Palette {
    tertiary: {
        main: string;
        light: string;
        dark: string;
    },
    neutral_0: string;
    neutral_1: string;
    neutral_2: string;
    neutral_3: string;
    neutral_4: string;
    neutral_5: string;
    complementary_1: Complementary; 
    complementary_2: Complementary; 
    complementary_3: Complementary; 
    complementary_4: Complementary; 
    complementary_5: Complementary; 
    complementary_6: Complementary; 
    complementary_7: Complementary;
    complementary_8: Complementary;
    complementary_9: Complementary;
    shadowBackground: string;
    loginBackground: string;
    layoutBackground: string;
}
interface AppThemeOptions extends ThemeOptions {
    palette: AppPalette;
    applogo: string;
    applogoTablet: string;
    applogoLogin: string;
}
export interface AppTheme extends Theme {
    palette: AppPalette;
    applogo: string;
    applogoTablet: string;
    applogoLogin: string;
}
  



export const createAppTheme = (dark: boolean): AppTheme => {

    let theme: AppTheme;

    theme = createTheme({
        typography: {
            fontFamily:[
                'Montserrat',
                'sans-serif',
            ].join(','),
    
            button: {
                textTransform: 'none'
            },
        },
        palette: {
            primary: {
                main: dark ? PRIMARY_DARK : PRIMARY,
            },
            secondary: {
                main: dark ? SECONDARY_DARK : SECONDARY,
            },
            error: {
                main: dark ? ERROR_DARK : ERROR,
            },
            warning: {
                main: dark ? WARNING_DARK : WARNING,
            },
            success: {
                main: dark ? SUCCESS_DARK : SUCCESS,
            },

            tertiary: {
                main: TERTIARY,
                light: TERTIARY,
                dark: TERTIARY
            },
            neutral_0: dark ? NEUTRAL_0_DARK : NEUTRAL_0,
            neutral_1: dark ? NEUTRAL_1_DARK : NEUTRAL_1,
            neutral_2: dark ? NEUTRAL_2_DARK : NEUTRAL_2,
            neutral_3: dark ? NEUTRAL_3_DARK : NEUTRAL_3,
            neutral_4: dark ? NEUTRAL_4_DARK : NEUTRAL_4,
            neutral_5: dark ? NEUTRAL_5_DARK : NEUTRAL_5,

            complementary_1: dark ? COMPLEMENTARY_1_DARK : COMPLEMENTARY_1,
            complementary_2: dark ? COMPLEMENTARY_2_DARK : COMPLEMENTARY_2,
            complementary_3: dark ? COMPLEMENTARY_3_DARK : COMPLEMENTARY_3,
            complementary_4: dark ? COMPLEMENTARY_4_DARK : COMPLEMENTARY_4,
            complementary_5: dark ? COMPLEMENTARY_5_DARK : COMPLEMENTARY_5,
            complementary_6: dark ? COMPLEMENTARY_6_DARK : COMPLEMENTARY_6,
            complementary_7: dark ? COMPLEMENTARY_7_DARK : COMPLEMENTARY_7,
            complementary_8: dark ? COMPLEMENTARY_8_DARK : COMPLEMENTARY_8,
            complementary_9: dark ? COMPLEMENTARY_9_DARK : COMPLEMENTARY_9,

            shadowBackground: dark ? "rgba(205, 205, 205, 0.5)" : "rgba(128, 128, 128, 0.5)",
            loginBackground: dark ? LOGIN_BACKGROUND_DARK : LOGIN_BACKGROUND,
            layoutBackground: dark ? BACKGROUND_DARK : BACKGROUND,
        },
        applogo: APP_LOGO,
        applogoTablet: APP_LOGO_TABLET,
        applogoLogin: APP_LOGO_LOGIN
    } as AppThemeOptions) as AppTheme;

    theme.typography.body1 = {
        fontFamily: "Montserrat",
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "20px",
        color: theme.palette.neutral_0
    }
    
    theme.typography.body2 = {
        fontFamily: "Montserrat",
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "24px",
        color: theme.palette.neutral_0
    }
    
    theme.typography.body3 = {
        fontFamily: "Montserrat",
        fontSize: "12px",
        fontWeight: 400,
        lineHeight: "20px",
        color: theme.palette.neutral_0
    };
    
    theme.typography.h1 = {
        fontFamily: "Montserrat",
        fontSize: "30px",
        fontWeight: 700,
        lineHeight: "35px",
        color: theme.palette.neutral_0,
        [theme.breakpoints.down('md')]: {
            fontSize: '25px',
        },
    };
    
    theme.typography.h2 = {
        fontFamily: "Montserrat",
        fontSize: "26px",
        fontWeight: 700,
        lineHeight: "30px",
        color: theme.palette.neutral_0,
        [theme.breakpoints.down('md')]: {
            fontSize: '22px',
        },
    };
    
    theme.typography.h3 = {
        fontFamily: "Montserrat",
        fontSize: "24px",
        fontWeight: 500,
        lineHeight: "26px",
        color: theme.palette.neutral_0,
        [theme.breakpoints.down('md')]: {
            fontSize: '18px',
        },
    };
    
    theme.typography.h4 = {
        fontFamily: "Montserrat",
        fontSize: "22px",
        fontWeight: 700,
        lineHeight: "24px",
        color: theme.palette.neutral_0,
        [theme.breakpoints.down('md')]: {
            fontSize: '20px',
        },
    };
    
    theme.typography.h5 = {
        fontFamily: "Montserrat",
        fontSize: "20px",
        fontWeight: 500,
        lineHeight: "22px",
        color: theme.palette.neutral_0,
        [theme.breakpoints.down('md')]: {
            fontSize: '17px',
        },
    };
    
    theme.typography.h6 = {
        fontFamily: "Montserrat",
        fontSize: "18px",
        fontWeight: 500,
        lineHeight: "20px",
        color: theme.palette.neutral_0,
        [theme.breakpoints.down('md')]: {
            fontSize: '16px',
        },
    };
    
    theme.typography.caption = {
        fontFamily: "Montserrat",
        fontSize: "16px",
        fontWeight: 700,
        lineHeight: "24px",
        color: theme.palette.neutral_0
    }

    theme.typography.caption2 = {
        fontFamily: "Montserrat",
        fontSize: "14px",
        fontWeight: 700,
        lineHeight: "22px",
        color: theme.palette.neutral_0
    }
    
    theme.typography.button = {
        fontFamily: "Montserrat",
        fontSize: "14px",
        fontWeight: 500,
        lineHeight: "22px",
        color: theme.palette.neutral_0
    }

    theme.typography.button2 = {
        fontFamily: "Montserrat",
        fontSize: "8px",
        fontWeight: 500,
        lineHeight: "100%",
        color: theme.palette.neutral_0
    }
    
    return theme as AppTheme;
};
